/**
 * Client local time utilities for gameplay events.
 * 
 * All gameplay events require a client_local_time timestamp with timezone offset
 * to support accurate streak calculations across timezones.
 */

const DEFAULT_CLIENT_LOCALE = 'en-US';
const DEFAULT_CLIENT_TIMEZONE = 'UTC';

/**
 * Get client's local time in ISO 8601 format with timezone offset.
 * Required for all gameplay events to support streak calculations.
 * 
 * @returns {string} ISO 8601 datetime with offset, e.g., "2025-01-15T23:45:00+01:00"
 * 
 * @example
 * getClientLocalTime() // => "2025-01-15T23:45:00+01:00"
 */
export function getClientLocalTime() {
  const now = new Date();
  const offsetMinutes = now.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMins = Math.abs(offsetMinutes) % 60;
  // Note: getTimezoneOffset returns the opposite sign (negative for east of UTC)
  const sign = offsetMinutes <= 0 ? '+' : '-';
  const offset = `${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;
  
  const localISO = now.getFullYear() + '-' +
    String(now.getMonth() + 1).padStart(2, '0') + '-' +
    String(now.getDate()).padStart(2, '0') + 'T' +
    String(now.getHours()).padStart(2, '0') + ':' +
    String(now.getMinutes()).padStart(2, '0') + ':' +
    String(now.getSeconds()).padStart(2, '0') + offset;
  
  return localISO;
}

/**
 * Get the browser locale, falling back to a stable default.
 *
 * @returns {string} Browser locale, e.g. "it-IT"
 */
export function getClientLocale() {
  if (typeof navigator === 'undefined') {
    return DEFAULT_CLIENT_LOCALE;
  }

  const languages = Array.isArray(navigator.languages) ? navigator.languages : [];
  return navigator.language || languages[0] || DEFAULT_CLIENT_LOCALE;
}

/**
 * Get the browser timezone, falling back to UTC.
 *
 * @returns {string} IANA timezone identifier, e.g. "Europe/Rome"
 */
export function getClientTimezone() {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return DEFAULT_CLIENT_TIMEZONE;
  }

  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_CLIENT_TIMEZONE;
  } catch (error) {
    return DEFAULT_CLIENT_TIMEZONE;
  }
}

/**
 * Get the headers that identify the browser's local context.
 *
 * @returns {Record<string, string>} Client context headers
 */
export function getClientRequestMetadataHeaders() {
  return {
    'X-Client-Locale': getClientLocale(),
    'X-Client-Timezone': getClientTimezone(),
    'X-Client-Local-DateTime': getClientLocalTime(),
  };
}

export const getClientContextHeaders = getClientRequestMetadataHeaders;

/**
 * Get current date in YYYY-MM-DD format using client's local timezone.
 * 
 * @returns {string} Date in YYYY-MM-DD format
 * 
 * @example
 * getClientLocalDate() // => "2025-01-15"
 */
export function getClientLocalDate() {
  const now = new Date();
  return now.getFullYear() + '-' +
    String(now.getMonth() + 1).padStart(2, '0') + '-' +
    String(now.getDate()).padStart(2, '0');
}
